import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  FileText, Plus, Loader2, DollarSign, Calendar, 
  CheckCircle, Clock, AlertTriangle, Trash2, ExternalLink,
  CreditCard, Receipt, Eye
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-600', icon: Clock },
  partial: { label: 'Partial', color: 'bg-amber-600', icon: AlertTriangle },
  paid: { label: 'Paid', color: 'bg-green-600', icon: CheckCircle },
};

export default function StatementsDialog({ open, onClose, clients, profile, invoiceSettings, onViewStatement, onSendStatement }) {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    if (open) {
      loadStatements();
    }
  }, [open]);

  const loadStatements = async () => {
    setLoading(true);
    try {
      const res = await api.getStatements();
      setStatements(res.data);
    } catch (error) {
      console.error('Failed to load statements:', error);
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStatement = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setCreating(true);
    try {
      const res = await api.createStatement({ client_id: selectedClient });
      toast.success(`Statement ${res.data.statement_number} created`);
      setStatements([res.data, ...statements]);
      setShowCreateForm(false);
      setSelectedClient('');
    } catch (error) {
      console.error('Failed to create statement:', error);
      toast.error(error.response?.data?.detail || 'Failed to create statement');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteStatement = async (statementId) => {
    if (!confirm('Are you sure you want to delete this statement?')) return;

    try {
      await api.deleteStatement(statementId);
      toast.success('Statement deleted');
      setStatements(statements.filter(s => s.id !== statementId));
    } catch (error) {
      console.error('Failed to delete statement:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete statement');
    }
  };

  const handleRecordPayment = async (statementId) => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const res = await api.recordStatementPayment(statementId, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod || null
      });
      toast.success('Payment recorded');
      setStatements(statements.map(s => s.id === statementId ? res.data : s));
      setRecordingPayment(null);
      setPaymentAmount('');
      setPaymentMethod('');
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Filter clients that have outstanding invoices
  const clientsWithOutstanding = clients.filter(c => 
    (c.total_billed || 0) - (c.total_paid || 0) > 0
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Statements
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Create Statement Section */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
            {!showCreateForm ? (
              <Button onClick={() => setShowCreateForm(true)} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Generate New Statement
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Select a client to generate a statement for all their outstanding invoices.
                </p>
                <div className="flex gap-2">
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clientsWithOutstanding.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No clients with outstanding invoices
                        </SelectItem>
                      ) : (
                        clientsWithOutstanding.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} - {formatCurrency((client.total_billed || 0) - (client.total_paid || 0))} outstanding
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCreateStatement} disabled={creating || !selectedClient}>
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                  </Button>
                  <Button variant="outline" onClick={() => {
                    setShowCreateForm(false);
                    setSelectedClient('');
                  }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Statements List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : statements.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No statements yet</p>
              <p className="text-sm">Generate a statement for a client with outstanding invoices</p>
            </div>
          ) : (
            <div className="space-y-3">
              {statements.map(statement => {
                const status = statusConfig[statement.status] || statusConfig.open;
                const StatusIcon = status.icon;

                return (
                  <div 
                    key={statement.id}
                    className="border dark:border-slate-700 rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{statement.statement_number}</span>
                          <Badge className={`${status.color} text-white text-xs`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {statement.client?.name || 'Unknown Client'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {statement.invoice_summaries?.length || 0} invoices • 
                          Created {formatDate(statement.statement_date)}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-lg font-semibold">
                          {formatCurrency(statement.total_balance_due)}
                        </p>
                        <p className="text-xs text-slate-500">Balance Due</p>
                        {statement.statement_paid > 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            {formatCurrency(statement.statement_paid)} paid
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Invoice breakdown */}
                    {statement.invoice_summaries && statement.invoice_summaries.length > 0 && (
                      <div className="mt-3 pt-3 border-t dark:border-slate-700">
                        <p className="text-xs font-medium text-slate-500 mb-2">Included Invoices:</p>
                        <div className="space-y-1">
                          {statement.invoice_summaries.map(inv => (
                            <div key={inv.invoice_id} className="flex justify-between text-xs">
                              <span className={inv.balance_due <= 0 ? 'line-through text-slate-400' : ''}>
                                {inv.invoice_number}
                              </span>
                              <span className={inv.balance_due <= 0 ? 'text-green-600' : ''}>
                                {inv.balance_due <= 0 ? 'Paid' : formatCurrency(inv.balance_due)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-3 pt-3 border-t dark:border-slate-700 flex flex-wrap gap-2">
                      {statement.status !== 'paid' && (
                        <>
                          {recordingPayment === statement.id ? (
                            <div className="flex gap-2 flex-1">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                className="w-28"
                              />
                              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                <SelectTrigger className="w-28">
                                  <SelectValue placeholder="Method" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">Cash</SelectItem>
                                  <SelectItem value="check">Check</SelectItem>
                                  <SelectItem value="card">Card</SelectItem>
                                  <SelectItem value="ach">ACH</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={() => handleRecordPayment(statement.id)}>
                                Apply
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => {
                                setRecordingPayment(null);
                                setPaymentAmount('');
                                setPaymentMethod('');
                              }}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setRecordingPayment(statement.id)}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              Record Payment
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewStatement && onViewStatement(statement)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => onSendStatement && onSendStatement(statement)}
                      >
                        <Receipt className="h-3 w-3 mr-1" />
                        Send
                      </Button>

                      {statement.status === 'open' && !statement.payments?.length && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteStatement(statement.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
